import { SprayRecordChemicalRequest } from '../request-models/spray-record-chemical-request.model';
import { SprayRecordRequest } from '../request-models/spray-record-request.model';
import { omitUndefined } from './wire-value-utils';

/**
 * The hub request body for creating or updating a spray record. Copies only the allowlisted fields
 * (`SPRAY_RECORD_WRITABLE`, plus `changeDescription`) and drops the ones that are not set, so an update stays
 * partial; `null` (clear the field) is kept. Drone flight payloads are already allowlisted by
 * `buildDroneFlightPayload`.
 */
export function toSprayRecordRequest(fields: SprayRecordRequest): SprayRecordRequest {
  return omitUndefined<SprayRecordRequest>({
    fieldId: fields.fieldId,
    workOrderId: fields.workOrderId,
    applicationDate: fields.applicationDate,
    status: fields.status,
    notes: fields.notes,
    changeDescription: fields.changeDescription,
    certificationId: fields.certificationId,
    certificationName: fields.certificationName,
    certificationNumber: fields.certificationNumber,
    certificationState: fields.certificationState,
    certificationExpiration: fields.certificationExpiration,
    orgFaa44807Number: fields.orgFaa44807Number,
    orgFaa44807Expiration: fields.orgFaa44807Expiration,
    orgFaaPart137Number: fields.orgFaaPart137Number,
    orgFaaPart137Expiration: fields.orgFaaPart137Expiration,
    chemicals: fields.chemicals?.map(toSprayRecordChemicalRequest),
    droneFlights: fields.droneFlights,
    whatWasTreated: fields.whatWasTreated,
    targetPest: fields.targetPest,
    nozzleDescription: fields.nozzleDescription,
    nozzleToTargetDistance: fields.nozzleToTargetDistance,
    pumpPressure: fields.pumpPressure,
    travelSpeed: fields.travelSpeed,
    driftManagementPractices: fields.driftManagementPractices,
    applicationTypes: fields.applicationTypes,
    applicationTypeOther: fields.applicationTypeOther,
    totalVolume: fields.totalVolume,
    tankMixNotes: fields.tankMixNotes,
    weatherTemperature: fields.weatherTemperature,
    weatherHumidity: fields.weatherHumidity,
    weatherWindSpeed: fields.weatherWindSpeed,
    weatherWindDirection: fields.weatherWindDirection,
    weatherConditions: fields.weatherConditions,
    takeoffLandingDamage: fields.takeoffLandingDamage,
    takeoffLandingDamageDescription: fields.takeoffLandingDamageDescription,
    equipmentMalfunction: fields.equipmentMalfunction,
    equipmentMalfunctionType: fields.equipmentMalfunctionType,
    equipmentMalfunctionDescription: fields.equipmentMalfunctionDescription,
    hasAccident: fields.hasAccident,
    accidentType: fields.accidentType,
    accidentDescription: fields.accidentDescription,
    uaLossType: fields.uaLossType,
    reentryInterval: fields.reentryInterval,
    ppeRequired: fields.ppeRequired,
    rinseEquipmentPostApplication: fields.rinseEquipmentPostApplication,
    emergencyContactNumber: fields.emergencyContactNumber,
    stateEmergencyContactNumber: fields.stateEmergencyContactNumber,
    useFieldForLoadMix: fields.useFieldForLoadMix,
    loadMixLatitude: fields.loadMixLatitude,
    loadMixLongitude: fields.loadMixLongitude,
    visualObserver: fields.visualObserver,
    voTested: fields.voTested,
    notam: fields.notam,
    preFlightChecklist: fields.preFlightChecklist,
    bufferZonesVerified: fields.bufferZonesVerified,
  });
}

function toSprayRecordChemicalRequest(chemical: SprayRecordChemicalRequest): SprayRecordChemicalRequest {
  return omitUndefined<SprayRecordChemicalRequest>({
    chemicalId: chemical.chemicalId,
    name: chemical.name,
    epaNumber: chemical.epaNumber,
    rate: chemical.rate,
    rateValue: chemical.rateValue,
    rateUnit: chemical.rateUnit,
    ratePerUnit: chemical.ratePerUnit,
    source: chemical.source,
    templateName: chemical.templateName,
  });
}

/**
 * The value to send for an optional spray record field: the entered value, else (when updating) `null` so an
 * emptied field is cleared, else (when creating) `undefined` so it isn't sent. A real `0` is kept.
 */
export function optionalValue<T>(value: T | '' | null | undefined, isUpdate: boolean): T | null | undefined {
  if (value === '' || value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value))) {
    return isUpdate ? null : undefined;
  }
  return value;
}
